import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, PackageCheck, DollarSign, MapPin, Truck, X } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, Empty, money } from "@/components/common";

const STAGE = {
  requested: { label: "Awaiting parcel", cls: "pill--open" },
  received: { label: "Received", cls: "pill--shipped" },
  refunded: { label: "Refunded", cls: "pill--refunded" },
};

function RefundModal({ order, onClose, onDone }) {
  const full = Number(order.amount || 0);
  const [amount, setAmount] = useState(full.toFixed(2));
  const [busy, setBusy] = useState(false);
  const amt = parseFloat(amount);
  const invalid = isNaN(amt) || amt <= 0 || amt > full + 0.001;
  const isPartial = !invalid && amt < full - 0.001;

  const submit = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/refund`, isPartial ? { amount: Math.round(amt * 100) / 100 } : {});
      toast.success(`${isPartial ? "Partial refund" : "Refund"} of ${money(amt)} issued via Stripe — buyer notified`);
      onDone();
    } catch (e) { toast.error(e.message || "Refund failed"); setBusy(false); }
  };

  return (
    <div className="ss-modal-back" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()} data-testid="refund-modal">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ fontSize: 20 }}>Issue refund</h2>
          <button type="button" className="disc-search" style={{ width: 34, height: 34 }} onClick={onClose} data-testid="refund-close"><X size={18} /></button>
        </div>
        <p className="ss-muted" style={{ fontSize: 13, marginBottom: 16 }}>{order.item_title} · {order.buyer_name} · order total {money(full)}</p>
        <label className="ss-label">Refund amount</label>
        <div className="ss-field"><DollarSign size={16} className="ss-muted" /><input type="number" min="0" step="0.01" max={full} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount-input" /></div>
        <div style={{ display: "flex", gap: 8, margin: "10px 0 16px", flexWrap: "wrap" }}>
          <button type="button" className="ss-chip" onClick={() => setAmount(full.toFixed(2))} data-testid="refund-full-chip">Full ({money(full)})</button>
          <button type="button" className="ss-chip" onClick={() => setAmount((full / 2).toFixed(2))} data-testid="refund-half-chip">Half</button>
        </div>
        {invalid && <div style={{ fontSize: 12, color: "var(--danger, #dc2626)", marginBottom: 10 }} data-testid="refund-error">Enter an amount between $0.01 and {money(full)}.</div>}
        <button className="ss-btn ss-btn-primary ss-btn-block" disabled={busy || invalid} onClick={submit} data-testid="refund-confirm">
          {busy ? "Processing…" : isPartial ? `Refund ${money(amt || 0)} (partial)` : `Refund full ${money(full)}`}
        </button>
      </div>
    </div>
  );
}

export default function Returns() {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);
  const [refundOrder, setRefundOrder] = useState(null);

  const load = () => api.get("/seller/orders").then((d) => setOrders(d.orders || [])).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);
  if (orders === null) return <Loading />;

  const returns = orders.filter((o) => o.return_status);

  const markReceived = async (id) => {
    setBusy(id + "received");
    try {
      await api.post(`/seller/returns/${id}/received`);
      toast.success("Marked as received — buyer notified");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  return (
    <div className="fade-up">
      <PageHead title="Returns" sub="Incoming returns with prepaid labels. Mark a parcel received, then approve the refund." />
      {returns.length === 0 ? (
        <Empty icon={RotateCcw} title="No returns" sub="Return requests from buyers will show up here." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {returns.map((o) => {
            const st = STAGE[o.return_status] || STAGE.requested;
            return (
              <div className="ss-card" style={{ padding: 16 }} key={o.id} data-testid={`return-${o.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 800 }}>{o.item_title}</div>
                    <div className="ss-muted" style={{ fontSize: 13 }}>{o.buyer_name} · {money(o.amount)}</div>
                    {o.return_reason && <div style={{ fontSize: 13, marginTop: 4 }}>“{o.return_reason}”</div>}
                  </div>
                  <span className={`ss-pill ${st.cls}`} data-testid={`return-stage-${o.id}`}>{st.label}</span>
                </div>

                {o.return_tracking_number && (
                  <div style={{ marginTop: 10, padding: 10, background: "var(--gold-soft)", borderRadius: 10, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
                    <Truck size={14} style={{ color: "var(--gold-2)" }} />
                    <span>Prepaid return label · <b>{o.return_carrier}</b> · {o.return_tracking_number}</span>
                  </div>
                )}

                {o.return_status !== "refunded" && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    {o.return_status === "requested" && (
                      <button className="ss-btn ss-btn-ghost ss-btn-sm" disabled={busy === o.id + "received"} onClick={() => markReceived(o.id)} data-testid={`return-received-${o.id}`}>
                        <PackageCheck size={14} /> Mark received
                      </button>
                    )}
                    <button className="ss-btn ss-btn-primary ss-btn-sm" onClick={() => setRefundOrder(o)} data-testid={`return-refund-${o.id}`}>
                      <DollarSign size={14} /> Refund
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {refundOrder && (
        <RefundModal
          order={refundOrder}
          onClose={() => setRefundOrder(null)}
          onDone={() => { setRefundOrder(null); load(); }}
        />
      )}
    </div>
  );
}
